import { planSpaceRows } from "@/lib/virtualspace/space-row-planner";
import type { HeadingConfig, VirtualSpaceBlockRow } from "@/lib/virtualspace/virtual-space-types";

export type PublicVirtualSpaceRailSection = {
  id: string;
  /** Short label for the rail dot peek strip — operator-supplied title or a
   *  neutral "Section N" fallback. Never an internal block-type slug. */
  label: string;
  /** Richer name for `aria-label` / `title` (operator title or "Section N"). */
  ariaLabel?: string;
  /** Stable index into the renderer's block list — used by the rail to scroll
   *  via `[data-vs-rail-block-id]` lookup. */
  blockIndex?: number;
};

/**
 * Legacy "Intro" rail entry. Kept exported for back-compat with any
 * external callers; the renderer no longer surfaces it because the
 * Copy link / Share controls now live in the page header next to the
 * theme toggle (header is the natural home for share affordances; the
 * intro strip duplicated them in the body and pushed real content
 * below the fold).
 */
export const PUBLIC_VIRTUAL_SPACE_INTRO_SECTION = {
  id: "share",
  label: "Intro",
  ariaLabel:
    "Intro — copy or share a link before scrolling through the spaces below",
} as const satisfies PublicVirtualSpaceRailSection;

function truncateRailLabel(s: string, max = 46): string {
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

/**
 * Block types that are decorative-only (visual rhythm) and should NEVER show
 * up in the rail nav — clicking "Spacer" leaks operator vocabulary and isn't
 * a destination customers want.
 */
const RAIL_HIDDEN_BLOCK_TYPES = new Set<string>(["spacer"]);

/**
 * Human-readable detail for one block. Resolution order (most-specific
 * first, matches `.cursorrules` "operator vocabulary" guidance):
 *
 *   1. `nav_label` — operator-supplied dedicated rail label (migration
 *      352). Wins over everything because the operator set it
 *      explicitly for the rail.
 *   2. Heading-block text — `config.text` for `block_type = "heading"`.
 *   3. `null` so the caller can fall back to a neutral "Section N" —
 *      we never leak block-type slugs ("Hero Aerial", "External Embed",
 *      "Rich Text") to customers; those describe the CMS, not the
 *      content.
 */
export function labelForPublicVirtualSpaceBlock(
  block: VirtualSpaceBlockRow,
): string | null {
  if (block.nav_label) {
    const t = block.nav_label.trim();
    if (t) return t.length > 42 ? `${t.slice(0, 40)}…` : t;
  }
  if (block.block_type === "heading") {
    const cfg = block.config as HeadingConfig | null | undefined;
    const t = cfg?.text?.trim();
    if (t) return t.length > 42 ? `${t.slice(0, 40)}…` : t;
  }
  return null;
}

/**
 * Accessible name for a content `<section>` — each block is one **space**
 * in the forward walk (space 1 of N, …). The aria text uses the operator's
 * title when available, otherwise the neutral "Section N" so screen readers
 * never announce internal block slugs.
 */
export function publicVirtualSpaceBlockSectionAriaLabel(
  block: VirtualSpaceBlockRow,
  spaceNumber: number,
  totalContentSpaces: number,
): string {
  const detail = labelForPublicVirtualSpaceBlock(block) ?? `Section ${spaceNumber}`;
  return `Section ${spaceNumber} of ${totalContentSpaces}, ${detail}`;
}

/**
 * Scroll rail plan — post-353: one entry per **Space row**, not per
 * individual block. The renderer paints Space rows (one row of the
 * 12-col grid each); the rail navigates between Space rows. Labels
 * resolve from the row's first block (`anchorBlock`) using the
 * existing precedence (`nav_label` → heading text → `Section N`),
 * so a row of two half-width blocks shows the FIRST block's label
 * — which is what the operator's working from when they set
 * `nav_label` on the leading block of a side-by-side pair.
 *
 * Pre-353 the rail had one entry per block. Today every legacy
 * block has `col_span = 12` (default from migration 353) so each
 * Space row contains exactly one block — the rail behaves
 * identically in steady state. As operators start composing
 * side-by-side rows, the rail collapses to one entry per row and
 * the operator's `nav_label` on the lead block names the whole
 * Space.
 *
 * Spacer-only Space rows are skipped entirely (decorative rhythm,
 * not navigable destinations). When a Space row contains a mix
 * of spacer + content blocks, the rail uses the first non-spacer
 * for label resolution, falling back to the spacer's anchor only
 * when the row is all-spacer (which then drops it).
 *
 * Pre-2026-05-06 the rail prepended a synthetic "Intro" entry
 * pointing at a Copy link / Share strip in the body. Those
 * controls now live in the page header (next to the theme
 * toggle) so the intro entry is no longer needed; the rail
 * starts at the first content Space row.
 */
export function buildPublicVirtualSpaceRailSections(
  blocks: VirtualSpaceBlockRow[],
): PublicVirtualSpaceRailSection[] {
  const spaceRows = planSpaceRows(blocks);
  const sections: PublicVirtualSpaceRailSection[] = [];

  spaceRows.forEach((spaceRow) => {
    // Pick the first non-spacer block as the label source. When
    // the whole row is decorative (all-spacer), the row is dropped
    // — the rail must never lead a customer to a "Spacer" entry.
    const labelSource =
      spaceRow.blocks.find(
        (b) => !RAIL_HIDDEN_BLOCK_TYPES.has(b.block_type),
      ) ?? null;
    if (!labelSource) return;

    const n = sections.length + 1;
    const detail = labelForPublicVirtualSpaceBlock(labelSource);
    const fallback = `Section ${n}`;
    const label = truncateRailLabel(detail ?? fallback);
    sections.push({
      // Anchor the rail's scroll target on the row's first block —
      // that's the element the renderer stamps with
      // `data-vs-rail-block-id`. Stays stable across resorts.
      id: spaceRow.anchorBlock.id,
      label,
      ariaLabel: detail ? `${detail} — section ${n}` : fallback,
      blockIndex: spaceRow.spaceIndex,
    });
  });

  return sections;
}
